import type { ReactNode, SVGProps } from 'react';

/**
 * The glyph that names a case's type on the queue row.
 *
 * **Five types, five glyphs, transcribed from the UI/UX pack path for path.**
 * The type rides a muted line beneath the reason rather than a second badge:
 * two adjacent badges make the **state** — the thing an operator is scanning
 * for — compete with a label they are not.
 *
 * None of the five carries a reading direction, so none is mirrored. A type the
 * console has no glyph for renders its label with no mark rather than borrowing
 * another type's — a wrong glyph is a wrong claim about what the row is.
 */
function TypeIcon({ children, ...props }: SVGProps<SVGSVGElement> & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className="shrink-0"
      {...props}
    >
      {children}
    </svg>
  );
}

const TYPE_GLYPHS: Readonly<Record<string, ReactNode>> = {
  /** A speech bubble — someone wrote in. */
  SUPPORT_TICKET: <path d="M4 5h16v11H8l-4 3z" />,
  /** A flag planted on a line — something was raised against an item. */
  CONTENT_REPORT: (
    <>
      <path d="M4 20V4h9l.6 2H20v9h-6.4l-.6-2H6" />
      <path d="M4 20v-4" />
    </>
  ),
  /** A person — the subject is an account rather than a title. */
  USER_REPORT: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 20a7.5 7.5 0 0115 0" />
    </>
  ),
  /** A search — something was looked for and not found. */
  CONTENT_REQUEST: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M16.5 16.5L21 21" />
    </>
  ),
  /** A warning triangle — something is broken. */
  DEFECT_REPORT: (
    <>
      <path d="M12 4l8 15H4z" />
      <path d="M12 10v4M12 16.5v.2" />
    </>
  ),
};

export function CaseTypeGlyph({ caseType }: { caseType: string }) {
  const glyph = TYPE_GLYPHS[caseType];
  if (!glyph) return null;
  return <TypeIcon>{glyph}</TypeIcon>;
}

/** A section of a case surface — a heading row with its optional action, then its body. */
export function CaseSection({
  id,
  heading,
  action,
  children,
}: {
  id: string;
  heading: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={id} className="mt-section-gap first:mt-0">
      <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
        <h2 id={id} className="font-display text-h2 font-bold">
          {heading}
        </h2>
        {action ? <span className="ms-auto">{action}</span> : null}
      </div>

      {children}
    </section>
  );
}

/**
 * The content-column action button — the shared primitive, under this area's
 * name. The decision's three buttons are drawn with it, and so are the comment
 * queue's, which is why it sits in the shared ring rather than in either area.
 */
export { ColumnButton } from '@shared/ui/ColumnButton';
